import { useContext, useEffect, useRef } from "react";
import { AuthContext } from "../../context/Auth/AuthContext";
import ColorModeContext from "../../layout/themeContext";

/**
 * Sincroniza el tema con la sesión (doc 0390).
 *
 * EL PROBLEMA QUE RESUELVE
 *
 * El estado del tema vive en `App.js`, porque el `ThemeProvider` de Material-UI
 * tiene que envolver toda la aplicación. Pero el `AuthProvider` está *dentro* de
 * `Routes`, es decir, por debajo. Consecuencia: cuando `App.js` pide el tema al
 * montarse, todavía no hay sesión.
 *
 *   abre la aplicación  →  GET /theme → 401 → se usa la instantánea de reserva
 *   inicia sesión       →  nadie vuelve a preguntar
 *
 * El tema de reserva basta para pintar, así que el fallo pasaba desapercibido…
 * hasta que se abría Configuración → Apariencia y la lista de temas salía vacía:
 * la petición que la llena también se había hecho sin sesión.
 *
 * Solo se notaba entrando desde el formulario de acceso. Recargando con la sesión
 * ya abierta funcionaba, porque entonces sí había token al montar.
 *
 * LA SOLUCIÓN
 *
 * Este componente no pinta nada: vive dentro del `AuthProvider` y avisa al tema
 * cuando aparece un usuario. Es el puente entre las dos capas.
 */
const ThemeSync = () => {
  const { isAuth, user } = useContext(AuthContext);
  const { colorMode } = useContext(ColorModeContext);
  const ultimoUsuario = useRef(null);

  const userId = user && user.id ? user.id : null;
  const refreshTheme = colorMode && colorMode.refreshTheme;

  useEffect(() => {
    if (!isAuth || !userId || !refreshTheme) return;

    // Solo al cambiar de usuario: evita repetir la petición en cada render y
    // cubre el cambio de cuenta sin recargar la página.
    if (ultimoUsuario.current === userId) return;
    ultimoUsuario.current = userId;

    refreshTheme();
  }, [isAuth, userId, refreshTheme]);

  // Al cerrar sesión se olvida el usuario, para que un acceso posterior con otra
  // cuenta vuelva a pedir su tema.
  useEffect(() => {
    if (!isAuth) ultimoUsuario.current = null;
  }, [isAuth]);

  return null;
};

export default ThemeSync;
